import { useMemo, useState } from "react";
import { CrudFilters, useUpdate } from "@refinedev/core";
import { QuoteCard } from "@/components/quotes/QuoteCard";
import { DragEndEvent, DragOverlay, DragStartEvent } from "@dnd-kit/core";
import KanbanColumnSkeleton from "@/components/skeleton/kanban";
import { KanbanBoard } from "@/components/tasks/kanban/board";
import KanbanColumn from "@/components/tasks/kanban/column";
import { getPrioritizedStatus, getStatuses } from "@/utilities";
import { Flex, Spin, message } from "antd";
import { isAllowedQuoteStateTransition } from "../utils/stateTransitions";

interface QuotesKanbanViewProps {
  t: any;
  dataSource: any[];
  isLoading: boolean;
  isFetching: boolean;
  filters: CrudFilters;
  setAllQuotes?: any;
  setFilters: (filters: CrudFilters, mode?: "replace" | "merge") => void;
}

export const QuotesKanbanView = ({
  t,
  dataSource,
  isLoading,
  filters,
  setAllQuotes,
  setFilters,
}: QuotesKanbanViewProps) => {
  const { mutate: updateQuote } = useUpdate();

  const [isDragging, setIsDragging] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draggedQuote, setDraggedQuote] = useState<any | null>(null);

  const STATUSES = getStatuses(t);

  const COLUMN_ORDER = [
    "quoting",
    "customer",
    "follow",
    "buy_on",
    "buy_off",
    "lost",
    "archived",
  ];

  const quoteStages = useMemo(() => {
    if (!dataSource || dataSource.length === 0)
      return { unassignedStage: [], columns: [] };

    const assignedIds = new Set();

    const unassignedStage = dataSource.filter((quote) => {
      if (!quote.quoteSellers || quote.quoteSellers.length === 0) return true;
      const isUnassigned = quote.quoteSellers.every(
        (seller: any) => seller.state === null,
      );
      if (isUnassigned) assignedIds.add(quote.id);
      return isUnassigned;
    });

    const grouped = STATUSES.map((status) => {
      const quotes = dataSource.filter((quote) => {
        if (assignedIds.has(quote.id)) return false;
        if (!quote.quoteSellers || quote.quoteSellers.length === 0)
          return false;

        const states = quote.quoteSellers
          .map((seller: any) => seller.state)
          .filter((state: any) => state !== null);

        if (states.length === 0) return false;

        const prioritizedStatus = getPrioritizedStatus(
          states.map((state: any) => ({ state })),
        );

        // Assisted purchases (react-admin place order) live in the Comprado
        // (buy_on) column — there's no dedicated bought_assisted column.
        const columnStatus =
          prioritizedStatus === "bought_assisted" ? "buy_on" : prioritizedStatus;

        const isMatch = columnStatus === status.id;

        if (isMatch) {
          assignedIds.add(quote.id);
          return true;
        }
        return false;
      });
      return { ...status, quotes };
    }).sort((a, b) => COLUMN_ORDER.indexOf(a.id) - COLUMN_ORDER.indexOf(b.id));

    return { unassignedStage, columns: grouped };
  }, [dataSource, STATUSES]);

  const updateQuoteState = async (
    id: string,
    state: string | null | undefined,
  ) => {
    try {
      await updateQuote({
        resource: "quotes",
        id,
        values: { state },
        successNotification: {
          message: "",
          description: "Quote status updated successfully",
          type: "success",
        },
        errorNotification: {
          message: "",
          description: t("quote.kanban.error", { state }),
          type: "error",
        },
      });

      setAllQuotes?.((quotes: any) =>
        quotes.map((q: any) => {
          if (q.id !== id) return q;
          if (q.quoteSellers?.length === 1) {
            return {
              ...q,
              quoteSellers: [{ ...q.quoteSellers[0], state }],
            };
          }
          return {
            ...q,
            quoteSellers: q.quoteSellers.map((s: any) => ({ ...s, state })),
          };
        }),
      );
    } catch (error: any) {
      console.error("Error al actualizar la cotización:", error);
    }
  };

  const handleOnDragStart = (event: DragStartEvent) => {
    const id = event.active.id as string;
    setActiveId(id);
    const quote = dataSource.find((q) => q.id === id);
    setDraggedQuote(quote);
  };

  const handleOnDragEnd = (event: DragEndEvent) => {
    setIsDragging(false);
    setActiveId(null);
    setDraggedQuote(null);
    let stageId = event.over?.id as string | null | undefined;
    const quoteId = event.active.id as string;
    const quoteStageId = event.active.data.current?.state;

    if (quoteStageId === stageId) return;
    if (stageId === "unassigned") stageId = null;

    // Block forbidden transitions before the optimistic update fires.
    // The Kanban board uses the prioritized state of the quote as its
    // current state (matches how columns are bucketed in `getStatuses`).
    if (stageId && !isAllowedQuoteStateTransition(quoteStageId, stageId)) {
      message.warning(
        `No se puede pasar de ${quoteStageId} a ${stageId}`,
      );
      return;
    }

    updateQuoteState(quoteId, stageId);
    setDraggedQuote(null);
  };

  if (isLoading || dataSource.length === 0)
    return (
      <Spin spinning={isLoading}>
        <PageSkeleton />
      </Spin>
    );

  return (
    <Spin spinning={isLoading}>
      <Flex style={{ width: "100%", overflowX: "auto", height: "75vh" }}>
        <KanbanBoard
          onDragEnd={handleOnDragEnd}
          onDragStart={handleOnDragStart}
        >
          {quoteStages.columns.map((column) => (
            <KanbanColumn
              key={column.id}
              id={column.id}
              title={column.id}
              count={column.quotes.length}
              quotes={column.quotes}
              isDragging={isDragging}
              setIsDragging={setIsDragging}
              setActiveId={setActiveId}
              setDraggedQuote={setDraggedQuote}
              activeId={activeId}
            />
          ))}
          <DragOverlay>
            {draggedQuote && <QuoteCard {...draggedQuote} />}
          </DragOverlay>
        </KanbanBoard>
      </Flex>
    </Spin>
  );
};

const PageSkeleton = () => {
  const columnCount = 5;
  const itemCount = 3;
  return (
    <div style={{ display: "flex", gap: 16, width: "100%" }}>
      {Array.from({ length: columnCount }).map((_, index) => (
        <KanbanColumnSkeleton key={index}>
          {Array.from({ length: itemCount }).map((_, idx) => (
            <div key={idx} />
          ))}
        </KanbanColumnSkeleton>
      ))}
    </div>
  );
};
